import { useState, ReactNode, useRef, useEffect } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logoutUser } from '../../store/slices/authSlice';
import { StratEduLogo } from '../brand/StratEduLogo';
import { Button } from '../common/Button';
import { navIconForItem, NavIcon } from '../icons/NavIcon';

interface NavigationItem {
  name: string;
  href: string;
  active?: boolean;
  permissions?: string[];
}

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  currentPage?: string;
}

const defaultNavigation: NavigationItem[] = [
  { name: 'Overview', href: '/dashboard' },
  {
    name: 'Evaluations',
    href: '/evaluations',
    permissions: [
      APP_PERMISSIONS.CREATE_EVALUATION,
      APP_PERMISSIONS.READ_EVALUATION,
      APP_PERMISSIONS.UPDATE_EVALUATION,
      APP_PERMISSIONS.CONDUCT_EVALUATION,
    ],
  },
  {
    name: 'Analytics',
    href: '/analytics',
    permissions: [
      APP_PERMISSIONS.VIEW_ANALYTICS,
      APP_PERMISSIONS.VIEW_SCHOOL_ANALYTICS,
      APP_PERMISSIONS.VIEW_DISTRICT_ANALYTICS,
      APP_PERMISSIONS.VIEW_STATE_ANALYTICS,
    ],
  },
  {
    name: 'Interventions',
    href: '/interventions',
    permissions: [APP_PERMISSIONS.READ_INTERVENTION],
  },
  {
    name: 'Reports',
    href: '/reports',
    permissions: [
      APP_PERMISSIONS.VIEW_ANALYTICS,
      APP_PERMISSIONS.VIEW_SCHOOL_ANALYTICS,
      APP_PERMISSIONS.VIEW_DISTRICT_ANALYTICS,
      APP_PERMISSIONS.VIEW_STATE_ANALYTICS,
      APP_PERMISSIONS.EXPORT_REPORTS,
    ],
  },
  {
    name: 'Permission Slips',
    href: '/permission-slips',
    permissions: [APP_PERMISSIONS.READ_PERMISSION_SLIP],
  },
  {
    name: 'Parent Portal',
    href: '/parent-portal',
    permissions: [APP_PERMISSIONS.READ_PARENT_PORTAL],
  },
  {
    name: 'Announcements',
    href: '/announcements',
    permissions: [
      APP_PERMISSIONS.SEND_ANNOUNCEMENTS,
      APP_PERMISSIONS.MANAGE_COMMUNICATIONS,
    ],
  },
  {
    name: 'Users',
    href: '/users',
    permissions: [APP_PERMISSIONS.READ_USER],
  },
  {
    name: 'Districts',
    href: '/districts',
    permissions: [APP_PERMISSIONS.READ_DISTRICT],
  },
  {
    name: 'Schools',
    href: '/schools',
    permissions: [APP_PERMISSIONS.READ_SCHOOL],
  },
  {
    name: 'Settings',
    href: '/settings',
    permissions: [APP_PERMISSIONS.MANAGE_SYSTEM_SETTINGS],
  },
];

const sidebarWidthOpen = 'w-60';
const sidebarWidthCollapsed = 'w-16';

export const DashboardLayout = ({
  children,
  title,
  subtitle,
  currentPage,
}: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const dispatch = useAppDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { user } = useAppSelector(state => state.auth);

  const getActivePage = () => {
    if (currentPage) return currentPage;
    const path = location.pathname;
    if (path === '/dashboard') return 'overview';
    if (path.startsWith('/evaluations')) return 'evaluations';
    if (path.startsWith('/analytics')) return 'analytics';
    if (path.startsWith('/interventions')) return 'interventions';
    if (path.startsWith('/permission-slips')) return 'permission-slips';
    if (path.startsWith('/reports')) return 'reports';
    if (path.startsWith('/parent-portal')) return 'parent portal';
    if (path.startsWith('/inventory')) return 'inventory';
    if (path.startsWith('/announcements')) return 'announcements';
    if (path.startsWith('/users')) return 'users';
    if (path.startsWith('/districts')) return 'districts';
    if (path.startsWith('/schools')) return 'schools';
    if (path.startsWith('/settings')) return 'settings';
    return 'overview';
  };

  const activePage = getActivePage();
  const navigation = defaultNavigation
    .filter(
      item =>
        !item.permissions ||
        item.permissions.some(permission => hasPermission(permission))
    )
    .map(item => ({
      ...item,
      active: item.name.toLowerCase() === activePage.toLowerCase(),
    }));

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      localStorage.removeItem('persist:auth');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.removeItem('persist:auth');
      navigate('/login', { replace: true });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    return user?.full_name || user?.username || 'User Name';
  };

  const getUserRole = () => {
    if (user?.role) {
      return user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'Administrator';
  };

  const mainOffset = sidebarOpen ? 'ml-60' : 'ml-16';

  return (
    <div className="min-h-screen flex bg-[var(--se-porcelain)]">
      <aside
        className={`fixed left-0 top-0 z-10 flex h-screen max-h-screen flex-shrink-0 flex-col bg-strat-midnight text-white transition-[width] duration-200 ${
          sidebarOpen ? sidebarWidthOpen : sidebarWidthCollapsed
        }`}
        aria-label="Dashboard navigation"
      >
        <div className="flex h-full flex-col p-3">
          <div className="mb-6 flex items-center justify-between gap-2 px-1 pt-1">
            <div className="min-w-0 flex-1">
              {sidebarOpen ? (
                <StratEduLogo layout="lockup" appearance="dark" className="max-w-[180px]" />
              ) : (
                <div className="flex justify-center">
                  <StratEduLogo layout="mark" appearance="dark" />
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              className="shrink-0 p-2 text-white/70 hover:bg-white/10 hover:text-white"
            >
              <span className="text-base font-medium">
                {sidebarOpen ? '‹' : '›'}
              </span>
            </Button>
          </div>

          <nav
            aria-label="Main navigation"
            className="flex-1 overflow-y-auto pb-4"
          >
            <ul className="space-y-0.5">
              {navigation.map(item => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`group flex items-center gap-3 rounded-md px-2.5 py-2.5 text-sm transition-colors ${
                      item.active
                        ? 'bg-white/12 font-semibold text-white'
                        : 'font-medium text-white/70 hover:bg-white/8 hover:text-white'
                    }`}
                    aria-current={item.active ? 'page' : undefined}
                  >
                    <NavIcon
                      name={navIconForItem(item.name)}
                      className="shrink-0 opacity-90"
                    />
                    <span
                      className={`truncate transition-opacity ${sidebarOpen ? 'opacity-100' : 'sr-only'}`}
                    >
                      {item.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-auto flex-shrink-0 border-t border-white/10 pt-3">
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex w-full items-center rounded-md p-2 text-left transition-colors hover:bg-white/8 focus:outline-none focus-visible:ring-2 focus-visible:ring-strat-routeTeal focus-visible:ring-offset-2 focus-visible:ring-offset-strat-midnight"
                aria-label="User menu"
                aria-expanded={userDropdownOpen}
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-strat-routeTeal text-xs font-semibold text-white"
                  aria-hidden
                >
                  {getUserInitials()}
                </div>
                <div
                  className={`ml-2 min-w-0 flex-1 ${sidebarOpen ? '' : 'sr-only'}`}
                >
                  <p className="truncate text-sm font-medium text-white">
                    {getUserDisplayName()}
                  </p>
                  <p className="truncate text-xs text-white/55">
                    {getUserRole()}
                  </p>
                </div>
              </button>

              {userDropdownOpen && sidebarOpen && (
                <div className="absolute bottom-full left-0 right-0 z-50 mb-2 rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] py-1 shadow-md">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center px-3 py-2 text-left text-sm text-[var(--se-ink)] hover:bg-[var(--se-porcelain)]"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      <main
        className={`flex min-h-screen flex-1 flex-col transition-[margin] duration-200 ${mainOffset}`}
      >
        <header className="border-b border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] px-6 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-[var(--se-ink)]">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-[var(--se-ink-muted)]">
                  {subtitle}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-[var(--se-ink-muted)]">
              <span className="hidden sm:inline">{getUserDisplayName()}</span>
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--se-rule)] bg-[var(--se-porcelain)] text-xs font-semibold text-[var(--se-ink)]"
                aria-hidden
              >
                {getUserInitials()}
              </span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">{children}</div>
      </main>
    </div>
  );
};
